import { ComponentConfig, FieldLabel } from '@measured/puck';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { Switch, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import { TermsAndConditionComponent } from './termsAndCondition';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import WithAccordion from '../customComponents/CustomAccordion';
import { verticalAligns } from '../../puck/reusable-props/options';
import AddProduct, { CartProductsProps } from './appProduct';
import { AxiosInstance } from 'axios';
import { toolTipInfo } from '../../utils/constants';
import Select from '../../../components/common/select';

export type TermsAndConditionProps = {
  showHeader: boolean;
  headerContent: string;
  showAcknowledgeDropdown: boolean;
  acknowledgeContent: string;
  border: IBorderValueProps;
  customCheckbox: {
    defaultChecked: boolean;
    checkBoxColor: string;
    checkedBorderColor: string;
    uncheckedBorderColor: string;
    checkboxshape: string;
    checkboxSize: string;
    enrollmentStyle: string;
    checkMarkColor: string;
    checkedToggleColor: string;
    uncheckedToggleColor: string;
    toggleSize: string;
    checkMarkSize: string;
  };
  checkBoxVerticalAlign: string;
  addProduct: CartProductsProps | null;
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const TermsAndCondition = ({
  useAppDispatch,
  useAppSelector,
  baseService,
}: ReduxDispatchProps & {
  baseService: AxiosInstance;
} & ReduxSelectorProps): ComponentConfig<TermsAndConditionProps> => {
  return {
    label: (
      <CustomTooltip title="TermsAndSub">
        <span>Terms And Condition</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      showHeader: {
        type: 'radio',
        label: 'Show Header',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      headerContent: {
        ...QuillTextEditor('Header Content', true, 1).content,
      },
      showAcknowledgeDropdown: {
        type: 'radio',
        label: 'Show Dropdown Arrow',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      acknowledgeContent: {
        ...QuillTextEditor('Content', true, 2).content,
      },
      customCheckbox: {
        type: 'custom',
        label: 'Custom Checkbox',
        render: ({ value, onChange }) => {
          const options = [
            { label: 'Square', value: 'rounded' },
            { label: 'Circle', value: 'rounded-full' },
          ];
          return (
            <WithAccordion title="Custom Checkbox">
              <div className="flex gap-2 items-center justify-between">
                <label>Default Checked</label>
                <Switch
                  checked={value?.defaultChecked}
                  onChange={e => {
                    onChange({
                      ...value,
                      defaultChecked: e.target.checked,
                    });
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
              <ColorPicker
                label="Background Color"
                tooltip="backgroundColor"
                onChange={e =>
                  onChange({
                    ...value,
                    checkBoxColor: e,
                  })
                }
                value={value?.checkBoxColor}
              />
              <div>
                <ColorPicker
                  value={value?.checkedBorderColor}
                  onChange={color =>
                    onChange({ ...(value || {}), checkedBorderColor: color })
                  }
                  label="Checked Border Color"
                  tooltip="Checked Border Color"
                />
              </div>
              <div>
                <ColorPicker
                  value={value?.uncheckedBorderColor}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      uncheckedBorderColor: color,
                    })
                  }
                  label="Unchecked Border Color"
                  tooltip="Unchecked Border Color"
                />
              </div>
              <div>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip
                        title={'Checkbox Shape' as keyof typeof toolTipInfo}>
                        <span>Checkbox Shape</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div"
                  className="w-full">
                  <div className="w-full p-2">
                    <ToggleButtonGroup
                      value={value?.checkboxshape}
                      exclusive
                      className="w-full"
                      onChange={(_e, selectValue) => {
                        selectValue &&
                          onChange({ ...value, checkboxshape: selectValue });
                      }}>
                      {options.map((option, index: number) => (
                        <ToggleButton
                          key={index}
                          size="small"
                          value={option.value}
                          disableRipple
                          aria-label={option.value}
                          className="flex-1">
                          {option.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </div>
                </FieldLabel>
              </div>
              <div>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip
                        title={'Checkbox Size' as keyof typeof toolTipInfo}>
                        <span>Checkbox Size</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div"
                  className="w-full">
                  <input
                    type="number"
                    value={value?.checkboxSize || 16}
                    onChange={e =>
                      onChange({
                        ...value,
                        checkboxSize: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter checkbox size"
                  />
                </FieldLabel>
              </div>
              <div>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title="EnrollementStyle">
                        <span>Enrollement Style</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div"
                />
                <Select
                  value={value?.enrollmentStyle}
                  onChange={val => onChange({ ...value, enrollmentStyle: val })}
                  options={[
                    {
                      label: 'Checkbox',
                      value: 'checkbox',
                    },
                    {
                      label: 'Toggle',
                      value: 'toggle',
                    },
                  ]}
                />
              </div>
              <ColorPicker
                value={value?.checkMarkColor || '#ffffff'}
                onChange={color =>
                  onChange({
                    ...(value || {}),
                    checkMarkColor: color,
                  })
                }
                label={'Check Mark Color'}
                tooltip={'Check Mark Color'}
              />
              <ColorPicker
                value={value?.checkedToggleColor || '#000000'}
                onChange={color =>
                  onChange({
                    ...(value || {}),
                    checkedToggleColor: color,
                  })
                }
                label={'Checked Toggle Color'}
                tooltip={'Checked Toggle Color'}
              />
              <ColorPicker
                value={value?.uncheckedToggleColor || '#d1d5db'}
                onChange={color =>
                  onChange({
                    ...(value || {}),
                    uncheckedToggleColor: color,
                  })
                }
                label={'Unchecked Toggle Color'}
                tooltip={'Unchecked Toggle Color'}
              />
              <div>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip
                        title={'Toggle Size' as keyof typeof toolTipInfo}>
                        <span>Toggle Size</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div"
                  className="w-full">
                  <input
                    type="number"
                    value={value?.toggleSize || 20}
                    onChange={e =>
                      onChange({
                        ...value,
                        toggleSize: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter toggle size"
                  />
                </FieldLabel>
              </div>
              <div>
                <FieldLabel
                  label={
                    (
                      <CustomTooltip
                        title={'Check Mark Size' as keyof typeof toolTipInfo}>
                        <span>Check Mark Size</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }
                  el="div"
                  className="w-full">
                  <input
                    type="number"
                    value={value?.checkMarkSize || 16}
                    onChange={e =>
                      onChange({
                        ...value,
                        checkMarkSize: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder="Enter check mark size"
                  />
                </FieldLabel>
              </div>
            </WithAccordion>
          );
        },
      },
      checkBoxVerticalAlign: {
        label: 'Vertical Align',
        type: 'select',
        options: verticalAligns,
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      addProduct: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <AddProduct
            baseService={baseService}
            value={value}
            onChange={value => {
              onChange(value);
            }}
            useAppSelector={useAppSelector}
          />
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      customCheckbox: {
        defaultChecked: false,
        checkBoxColor: '#000000',
        checkedBorderColor: '#000000',
        uncheckedBorderColor: '#d1d5db',
        checkboxshape: 'rounded',
        checkboxSize: '16',
        enrollmentStyle: 'checkbox',
        checkMarkColor: '#ffffff',
        checkedToggleColor: '#000000',
        uncheckedToggleColor: '#d1d5db',
        toggleSize: '20',
        checkMarkSize: '16',
      },
      checkBoxVerticalAlign: 'items-center',
      ...borderProps,
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      showHeader: false,
      addProduct: null,
      headerContent:
        '<h3 style="line-height:1; style="font-size: 14px;"><span>Header Content</span></h3>',
      showAcknowledgeDropdown: false,
      acknowledgeContent:
        '<p style="line-height: 1.5;"><span style="font-size: 14px;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna</span></p>',
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
    },
    render: ({
      margin,
      showHeader,
      headerContent,
      showAcknowledgeDropdown,
      acknowledgeContent,
      showComponent,
      customCheckbox,
      border,
      padding,
      editMode,
      checkBoxVerticalAlign,
      addProduct,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);

      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`${border?.borderStyle?.[currentDeviceType]} w-full`}
              style={{
                ...marginStyles,
                ...getBorderProps(border, currentDeviceType),
              }}>
              <TermsAndConditionComponent
                showHeader={showHeader}
                headerContent={headerContent}
                showAcknowledgeDropdown={showAcknowledgeDropdown}
                acknowledgeContent={acknowledgeContent}
                padding={padding}
                currentDeviceType={currentDeviceType}
                backgroundColor={customCheckbox?.checkBoxColor}
                defaultChecked={customCheckbox?.defaultChecked}
                checkboxBorderColor={customCheckbox}
                checkboxshape={customCheckbox?.checkboxshape}
                checkboxSize={customCheckbox?.checkboxSize}
                enrollmentStyle={customCheckbox?.enrollmentStyle}
                checkMarkColor={customCheckbox?.checkMarkColor}
                checkedToggleColor={customCheckbox?.checkedToggleColor}
                uncheckedToggleColor={customCheckbox?.uncheckedToggleColor}
                toggleSize={customCheckbox?.toggleSize}
                checkMarkSize={customCheckbox?.checkMarkSize}
                checkBoxVerticalAlign={checkBoxVerticalAlign}
                useAppDispatch={useAppDispatch}
                useAppSelector={useAppSelector}
                addProduct={addProduct}
              />
            </div>
          )}
        </>
      );
    },
  };
};
